import { consumePushOpen } from '@/features/notifications/lib/pushOpen';
import { errorCodeOf, orderActionPayload } from '@/features/orders/utils/order-analytics';
import { trackEvent } from '@/lib/analytics/umami-client';
import type { OnlineOrder } from '@restaurant/api-client';
import { useEffect, useRef } from 'react';

/**
 * Report that an operator looked at an order.
 *
 * A pageview can't stand in for this: the inspector docks beside the list above
 * 1100px and only pushes `/orders/:id` on narrow screens, so counting pageviews
 * would only ever see phone traffic. `surface` keeps the two readable apart - a
 * docked open is a much lower-intent act than a pushed one.
 *
 * Keyed on the loaded order's id held in a ref, so the 20s list refetch (which
 * hands back a new object every time) and StrictMode's double-mount can't turn
 * one view into several.
 */
export function useTrackOrderOpened(
  order: OnlineOrder | undefined,
  surface: 'docked' | 'page',
): void {
  const trackedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!order || trackedIdRef.current === order.id) return;
    trackedIdRef.current = order.id;
    const via = consumePushOpen(`/orders/${order.id}`) ? 'push' : undefined;
    trackEvent(
      'order_action',
      orderActionPayload({ action: 'opened', order, outcome: 'ok', surface, via }, Date.now()),
    );
  }, [order, surface]);
}

export function trackOrderPrinted(
  order: OnlineOrder,
  result: { tickets: number } | { error: unknown },
): void {
  trackEvent(
    'order_action',
    orderActionPayload(
      'error' in result
        ? {
            action: 'printed',
            order,
            from: 'inspector',
            outcome: 'error',
            errorCode: errorCodeOf(result.error),
          }
        : { action: 'printed', order, from: 'inspector', outcome: 'ok', tickets: result.tickets },
      Date.now(),
    ),
  );
}
